// Answers to review (answer-quality spec, Phase 2a). No imports on purpose: `node --test` loads this file directly.

export const SIGNALS = ['nodata', 'fallback', 'error', 'weak_match', 'no_docs'] as const;
export type Signal = (typeof SIGNALS)[number];
export type ReviewStatus = 'open' | 'fixed' | 'ignored';

export const SIGNAL_LABEL: Record<Signal, string> = {
  nodata: 'No data',
  fallback: 'Fallback',
  error: 'Error',
  weak_match: 'Weak match',
  no_docs: 'No documents',
};

export interface ReviewMember {
  traceId: string;
  question: string;
  answer: string | null;
  outcome: string;
  signal: Signal;
  bestScore: number | null;
  createdAt: string;
  status: ReviewStatus;
  note: string | null;
}
export interface ReviewDocument {
  id: string;
  title: string | null;
  origin: string | null;
  exists: boolean;
}
export interface ReviewGroup {
  leaderTraceId: string;
  question: string;
  size: number;
  signals: Partial<Record<Signal, number>>;
  latestAt: string;
  status: ReviewStatus | 'mixed';
  members: ReviewMember[];
  documents: ReviewDocument[];
}
/** GET /api/answer-review/:system/:botId — `truncated` when more answers matched than the list holds. */
export interface ReviewGroups {
  groups: ReviewGroup[];
  truncated: boolean;
}
export interface DayMetrics {
  day: string;
  total: number;
  answered: number;
  nodata: number;
  fallback: number;
  error: number;
  weak: number;
  nodocs: number;
}
export interface Metrics {
  days: DayMetrics[];
  totals: DayMetrics & { fixed: number; ignored: number };
}
export interface ReviewFilters {
  days: number;
  status: ReviewStatus | 'all';
  signal: Signal | '';
  lowScore?: number;
}

export function signalClass(s: Signal): string {
  switch (s) {
    case 'nodata':
      return 'bg-amber-50 text-amber-800';
    case 'fallback':
      return 'bg-orange-50 text-orange-800';
    case 'error':
      return 'bg-red-50 text-red-700';
    case 'weak_match':
      return 'bg-blue-50 text-blue-700';
    case 'no_docs':
      return 'bg-purple-50 text-purple-700';
  }
}

export function rate(n: number, total: number): string {
  return total > 0 ? `${((n / total) * 100).toFixed(1)}%` : '—';
}

export function summary(t: Metrics['totals']) {
  return {
    total: t.total,
    noData: rate(t.nodata, t.total),
    fallback: rate(t.fallback, t.total),
    error: rate(t.error, t.total),
    weak: rate(t.weak, t.total),
    noDocs: rate(t.nodocs, t.total),
    fixed: t.fixed,
    ignored: t.ignored,
  };
}

export function reviewQuery(f: ReviewFilters): Record<string, string | undefined> {
  return {
    days: String(f.days),
    status: f.status,
    signal: f.signal || undefined,
    lowScore: f.lowScore != null ? String(f.lowScore) : undefined,
  };
}

/** Knowledge › Documents opened on one document (DocumentsPanel reads `?doc=`). */
export function docLink(system: string, botId: string, docId: string): string {
  return `/bots/${system}/${encodeURIComponent(botId)}/knowledge?doc=${encodeURIComponent(docId)}`;
}

/** The "fix the source, not the chunk" rule (spec §1.5): builder chunks are recreated by the next build. */
export function docHint(origin: string | null): string | null {
  return origin === 'doc_chunker'
    ? 'Built from a crawled page — fix it in Knowledge › Pages (edit, which locks the page, then re-chunk). An edit here is lost at the next build.'
    : null;
}

/**
 * Only the newest request may set state: filters change faster than the server answers, and a slow 30-day reply must
 * not land under "Today".
 */
export function latestGate(): { begin: () => number; isCurrent: (id: number) => boolean } {
  let latest = 0;
  return { begin: () => ++latest, isCurrent: (id) => id === latest };
}

export function memberCountLabel(n: number): string {
  return `${n} answer${n === 1 ? '' : 's'}`;
}
